import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import {
  FormSelect,
  FormSelectOptions
} from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetThemesQuery } from '../../../../../services/endpoints/content/theme';
import { Scopes } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { Theme } from '../../../../../types/theme.type';
import { themesColumns } from '../../../../../utils/consts/columnDefs/contentColumns';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { canEditThemes } from '../../../../../utils/rbac';

const defaultScope = 'all';

export const ThemesView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetThemesQuery(filters, {
    refetchOnMountOrArgChange: true
  });
  const [themes, setThemes] = useState<Theme[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const currentUser = getCurrentUser();

  const isPublicOptions: FormSelectOptions[] = [
    {
      value: '1',
      label: 'True'
    },
    {
      value: '0',
      label: 'False'
    }
  ];

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than'),
      element: (
        // <DateField name="created_at_from" label="Created At" />
        <DateFilterField
          name="created_at_from"
          label="Created At"
        />
      )
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than'),
      element: (
        // <DateField name="created_at_to" />
        <DateFilterField name="created_at_to" />
      )
    },
    {
      name: 'is_public',
      element: (
        <FormSelect
          name="is_public"
          label="Is Public"
          options={isPublicOptions}
          selected_Value={isPublicOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'is_public')
          )}
        />
      )
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setThemes(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Themes`}
        columns={themesColumns}
        data={themes}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="themes"
        filters={filters?.data?.filterBy}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          canEditThemes(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/content/themes/new')}
            >
              New Theme
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
